/**
 * Course registration form helpers:
 * 1. Auto-select a course from a `course` query parameter (e.g. /register?course=slug).
 * 2. Render the selected course's price below the course select.
 *
 * The price map and label strings are injected by templates/_blocks/forms.twig.
 */
const urlParams = new URLSearchParams(window.location.search)
const rawCourseParam = urlParams.get('course')

const readUidToIdMap = (): Record<string, string> => {
    const node = document.querySelector<HTMLScriptElement>('script[data-course-uid-to-id]')
    if (!node?.textContent) return {}
    try { return JSON.parse(node.textContent) } catch { return {} }
}

// Translate a UID to numeric entry ID if a mapping is present, otherwise pass through.
const courseParam = rawCourseParam
    ? (readUidToIdMap()[rawCourseParam] ?? rawCourseParam)
    : null

const readPrices = (): Record<string, number | null> => {
    const node = document.querySelector<HTMLScriptElement>('script[data-course-prices]')
    if (!node?.textContent) return {}
    try { return JSON.parse(node.textContent) } catch { return {} }
}

const readText = (selector: string): string => {
    return document.querySelector<HTMLElement>(selector)?.textContent?.trim() ?? ''
}

const formatPrice = (value: number): string =>
    new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(value)

const COURSE_SELECT_SELECTOR = 'select[name*="[course]"], input[name*="[course]"]'

const findCourseValue = (): string => {
    const select = document.querySelector<HTMLSelectElement>('select[name*="[course]"]')
    if (select) return select.value

    const checked = document.querySelector<HTMLInputElement>(
        'input[name*="[course]"]:checked'
    )
    return checked?.value ?? ''
}

const renderPriceBlock = (anchor: Element) => {
    if (document.querySelector('[data-course-price-block]')) return

    const label = readText('[data-course-price-label]') || 'Price'
    const suffix = readText('[data-course-price-suffix]') || 'Excluding VAT'

    const wrapper = document.createElement('div')
    wrapper.dataset.coursePriceBlock = '1'
    wrapper.className = 'mt-4 mb-4'
    wrapper.innerHTML = `
        <div class="text-md font-bold text-white">${label}</div>
        <div class="text-md text-white">
            <span data-course-price-value>–</span>
            <span class="text-sm text-greyscale-300 ml-2">${suffix}</span>
        </div>
    `

    const row = anchor.closest('.fui-row')
    if (row) {
        row.insertAdjacentElement('afterend', wrapper)
    } else {
        anchor.parentElement?.appendChild(wrapper)
    }

    updatePrice()
}

const updatePrice = () => {
    const valueEl = document.querySelector<HTMLElement>('[data-course-price-value]')
    if (!valueEl) return

    const prices = readPrices()
    const value = findCourseValue()
    const price = value ? prices[value] : null
    valueEl.textContent = price != null ? formatPrice(price) : '–'

    // Sync the hidden Formie coursePrice input so the value is submitted.
    const priceInput = document.querySelector<HTMLInputElement>('input[name*="[coursePrice]"]')
    if (priceInput) {
        priceInput.value = price != null ? String(price) : ''
    }
}

// Listen at the document level so we don't rely on holding a stale element reference.
document.addEventListener('change', (e) => {
    const target = e.target as HTMLElement | null
    if (target?.matches(COURSE_SELECT_SELECTOR)) {
        updatePrice()
    }
})

const observer = new MutationObserver((_mutations, obs) => {
    const courseSelect = document.querySelector<HTMLSelectElement>('select[name*="[course]"]')
    if (!courseSelect) return

    obs.disconnect()

    if (courseParam) {
        for (const option of courseSelect.options) {
            if (
                option.value === courseParam ||
                option.text.toLowerCase().includes(courseParam.toLowerCase())
            ) {
                courseSelect.value = option.value
                courseSelect.dispatchEvent(new Event('change', { bubbles: true }))
                break
            }
        }
    }

    renderPriceBlock(courseSelect)
})

observer.observe(document.body, { childList: true, subtree: true })

// Stop observing after 5 seconds to prevent memory leaks
setTimeout(() => observer.disconnect(), 5000)
